'use strict'
import { useCallback } from 'react'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { hasErrorClass } from '../extra/common'
import { useEmployeeCountDispatch, useEmployeeCountSelector } from '../extra/store'
import { ShowErrors } from './ShowErrors'
import { shallowEqual } from 'react-redux'

/*==================================
 定数定義
 ==================================*/
const CONCURRENT_SECONDMENT_TARGET_OPTIONS = [
  {
    value: '本務',
    label: '本務'
  },
  {
    value: '出向元',
    label: '出向元'
  }
]

/*==================================
 カスタムフック
 ==================================*/
/**
 * 兼務出向者の設定に関するロジックを管理するカスタムフック。
 *
 * @returns {{
 *   concurrentSecondmentTarget: string
 *   fiscalYearErrors: object
 *   handleChangeConcurrentSecondmentTarget: (event: { target: { value: string } }) => void
 * }}
 */
const useConcurrentSecondmentLogic = () => {
  const dispatch = useEmployeeCountDispatch()

  const { concurrentSecondmentTarget, fiscalYearErrors } = useEmployeeCountSelector((state) => {
    const selectedYear = state.employeeCountInfo.selectedFiscalYear
    const fiscalYearSettings = state.employeeCountInfo.fiscalYearSettings[selectedYear]

    return {
      concurrentSecondmentTarget: fiscalYearSettings?.employeeCountSetting?.[0]?.concurrentSecondmentTarget ?? '',
      fiscalYearErrors: fiscalYearSettings?.errors?.client ?? {}
    }
  }, shallowEqual)

  /**
   * 兼務出向者のカウント対象設定変更ハンドラ。
   *
   * @param {(event: { target: { value: string } }) => void} e - イベントオブジェクト => 選択された値 ('本務' or '出向元')
   * @returns {void}
   */
  const handleChangeConcurrentSecondmentTarget = useCallback(
    (e) => {
      const value = e?.target?.value ?? ''
      dispatch('CHANGE_CONCURRENT_SECONDMENT_TARGET', { concurrentSecondmentTarget: value })
    },
    [dispatch]
  )

  return { concurrentSecondmentTarget, fiscalYearErrors, handleChangeConcurrentSecondmentTarget }
}

/*==================================
 コンポーネント
 ==================================*/
/**
 * 兼務出向者の設定セクションをレンダリングするコンポーネント。
 *
 * @returns {React.ReactElement}
 * @component
 */
const ConcurrentSecondmentTargetSetting = () => {
  const { concurrentSecondmentTarget, fiscalYearErrors, handleChangeConcurrentSecondmentTarget } =
    useConcurrentSecondmentLogic()
  const selectClasses = 'w-[300px]'

  return (
    <>
      <div className="text-body-2-bold grow mt-6">■出向者の設定（兼務出向）</div>
      <div className="flex items-center justify-between mb-6">
        出向先が複数ある兼務出向の従業員を、出向元と本務企業のどちらの従業員としてカウントするかを設定します。
      </div>
      <div className="text-h6 bg-white font-Regular mt-2.5">
        <div className="flex form-control items-center">
          <ChrofyCustomSelect
            id="concurrent-secondment-target"
            name="concurrentSecondmentTarget"
            value={concurrentSecondmentTarget}
            onChange={handleChangeConcurrentSecondmentTarget}
            className={selectClasses}
            options={CONCURRENT_SECONDMENT_TARGET_OPTIONS}
            disabled={false}
            isError={hasErrorClass('concurrentSecondmentTarget', fiscalYearErrors)}
            hasNoSelect={false}
          />
          <span className="pl-2">の企業の従業員としてカウントする</span>
        </div>
        <div className="flex items-center justify-between text-[#3150a5] mt-6">
          ※カウント対象となった企業の社内兼務情報が登録されている場合、兼務区分が"主務"の部門の従業員としてカウントします
        </div>
      </div>

      <div className="mt-3">
        <ShowErrors clientErrors={fiscalYearErrors} fieldName="concurrentSecondmentTargetSetting" />
      </div>
    </>
  )
}

export default ConcurrentSecondmentTargetSetting
